import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Avatar, Chip } from '@mui/material';
import { History, Refresh, Error as ErrorIcon } from '@mui/icons-material';
import FilterDropdown from '../components/common/FilterDropdown';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import ListRowCard from '../components/common/ListRowCard';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import { useDebounce } from '../hooks/useDebounce';
import { useRecentActivities } from '../hooks/useWarehouseData';
import { WarehouseAPI } from '../services/api';

type ActivityFilter = 'all' | 'child-deduction' | 'bundle-creation' | 'debundle' | 'bundle-category-log' | 'image-generation';

// Distinct service-unavailable panel with a Retry affordance — mirrors the InventoryPage error
// state (the audit's gold standard) so an outage reads differently from a genuine empty list.
const ErrorPanel: React.FC<{ message?: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <Box
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', py: 6, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
      border: '1px dashed #ECECE4',
    }}
  >
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
      <ErrorIcon sx={{ color: '#DC2626' }} />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
      Couldn't load activity logs
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
      {message || 'The activity service may be unavailable. Please try again.'}
    </Typography>
    <Button
      variant="contained"
      disableElevation
      startIcon={<Refresh />}
      onClick={onRetry}
      sx={{ py: 1, px: 2.5, fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
    >
      Retry
    </Button>
  </Box>
);

const ActivityLogsPage: React.FC = () => {
  // Search/filter/page live in the URL (prefix "al", page stays 1-INDEXED) so a refresh restores
  // where the user left off. Defaults stay OUT of the URL; changing filter/search resets the page
  // to 1 in the SAME update (via useSetUrlParams).
  const setUrlParams = useSetUrlParams();
  // The search box stays controlled on `query`; the DEBOUNCED value is written to "alq" (seeded
  // from the URL on mount) so typing doesn't rewrite the URL on every keystroke.
  const [alq] = useUrlParam('alq', '');
  const [query, setQuery] = useState(alq);
  const [filterParam] = useUrlParam('alfilter', 'all');
  const filter = filterParam as ActivityFilter;
  const setFilter = (v: ActivityFilter) => setUrlParams({ alfilter: v === 'all' ? null : v, alpage: null });
  const debouncedQuery = useDebounce(query, 400);

  const [pageParam, setPageParam] = useUrlParam('alpage', '1');
  const page = parseInt(pageParam, 10) || 1;
  const setPage = (v: number) => setPageParam(v === 1 ? '1' : String(v));

  // Push the debounced value into the URL (resetting the page to 1 in the same update). Seeding the
  // input from "alq" above makes an initial match a no-op write.
  useEffect(() => {
    if (debouncedQuery === alq) return;
    setUrlParams({ alq: debouncedQuery === '' ? null : debouncedQuery, alpage: null });
  }, [debouncedQuery, alq, setUrlParams]);

  // Image generation logs (separate fetch — not covered by useRecentActivities). A fetch FAILURE
  // is tracked on `imageLogsError` so a service outage renders a distinct error+Retry panel rather
  // than collapsing into the genuine "no logs" empty state.
  const [imageLogs, setImageLogs] = useState<any[]>([]);
  const [imageLogsLoading, setImageLogsLoading] = useState(false);
  const [imageLogsError, setImageLogsError] = useState(false);
  const [imageLogsTotal, setImageLogsTotal] = useState(0);

  const fetchImageLogs = useCallback(() => {
    setImageLogsLoading(true);
    setImageLogsError(false);
    WarehouseAPI.getImageGenerationLogs(DEFAULT_PAGE_SIZE, page)
      .then(r => {
        setImageLogs(r.logs || []);
        setImageLogsTotal(r.total || 0);
      })
      .catch(() => {
        setImageLogs([]);
        setImageLogsError(true);
      })
      .finally(() => setImageLogsLoading(false));
  }, [page]);

  useEffect(() => {
    if (filter === 'image-generation' || filter === 'all') {
      fetchImageLogs();
    }
  }, [filter, fetchImageLogs]);

  const { data, isLoading, isError, refetch } = useRecentActivities(DEFAULT_PAGE_SIZE, page, filter === 'image-generation' ? 'all' : filter, debouncedQuery);

  const items = data?.data || [];
  const totalItems = data?.totalItems || 0;

  // Helper to get action description for bundle category logs
  const getActionDescription = (action: string) => {
    const actions: Record<string, string> = {
      'bundle-category-creation': 'created bundle category',
      'bundle-version-creation': 'created new version of',
      'bundle-child-addition': 'added child to',
      'bundle-child-removal': 'removed child from',
      'bundle-enabled': 'enabled',
      'bundle-disabled': 'disabled',
      'bundle-updated': 'updated',
      'bundle-bulk-upload': 'bulk uploaded',
      'bundle-full-update': 'edited details of',
      'bundle-version-update': 'edited version details of',
      'bundle-reorder-toggle': 'toggled reorder tag on',
    };
    return actions[action] || action;
  };

  return (
    <PageContainer>
      <PageHeader icon={<History />} title="Activity Logs" subtitle="Search and filter activity logs." />

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search (sku, bundle, user)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: 320 }}
          />

          <FilterDropdown
            label="Type"
            value={filter}
            onChange={(v) => setFilter(v as any)}
            allLabel="All"
            minWidth={220}
            options={[
              { value: 'child-deduction', label: 'Child Deductions' },
              { value: 'bundle-creation', label: 'Add Bundle' },
              { value: 'debundle', label: 'Reduce Bundle' },
              { value: 'bundle-category-log', label: 'New Bundle Creations' },
              { value: 'image-generation', label: 'AI Image Generation' },
            ]}
          />

          <Button variant="outlined" onClick={() => { refetch(); }}>Refresh</Button>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 'none', border: '1px solid #ECECE4' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* Image generation logs view */}
          {filter === 'image-generation' && (
            <Box sx={{ p: 2 }}>
              {imageLogsLoading && <Typography>Loading…</Typography>}
              {!imageLogsLoading && imageLogsError && <ErrorPanel onRetry={fetchImageLogs} />}
              {!imageLogsLoading && !imageLogsError && imageLogs.length === 0 && <Typography>No image generation logs found</Typography>}
              {!imageLogsLoading && !imageLogsError && imageLogs.map((log: any, idx: number) => (
                <Box key={log._id || idx} sx={{ mb: 2 }}>
                  <ListRowCard accent="#DC5829" index={idx}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, p: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body1">
                        <strong>{log.userId}</strong> generated an image using <strong>{log.modelUsed || 'unknown model'}</strong>
                      </Typography>
                      <Chip
                        label={log.success ? 'Success' : 'Failed'}
                        size="small"
                        sx={{ bgcolor: log.success ? '#dcfce7' : '#fee2e2', color: log.success ? '#15803d' : '#b91c1c', fontWeight: 700, fontSize: '0.65rem' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      SKUs: {(log.skus || []).join(', ') || '—'}
                      {log.costUSD > 0 && ` • Cost: $${Number(log.costUSD).toFixed(4)}`}
                      {(log.inputTokens > 0 || log.outputTokens > 0) && ` • Tokens: ${Number(log.inputTokens).toLocaleString()} in / ${Number(log.outputTokens).toLocaleString()} out`}
                    </Typography>
                    {log.imageUrl && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={log.imageUrl}
                          variant="rounded"
                          sx={{ width: 56, height: 56, border: '1px solid #ECECE4', cursor: log.imageUrl.startsWith('https://') ? 'pointer' : 'default' }}
                          onClick={() => log.imageUrl.startsWith('https://') && window.open(log.imageUrl, '_blank')}
                        />
                        {log.imageUrl.startsWith('https://') ? (
                          <Typography
                            component="a"
                            href={log.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{ color: '#B8451E', wordBreak: 'break-all' }}
                          >
                            {log.imageUrl}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            (legacy image — not stored in S3)
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {new Date(log.createdAt).toLocaleString()}
                  </Typography>
                    </Box>
                  </ListRowCard>
                </Box>
              ))}
            </Box>
          )}

          {/* Standard activity logs */}
          {filter !== 'image-generation' && (
            <Box sx={{ p: 2 }}>
              {isLoading && <Typography>Loading...</Typography>}
              {!isLoading && isError && <ErrorPanel message="The activity service may be unavailable. Please try again." onRetry={() => { refetch(); }} />}
              {!isLoading && !isError && items.length === 0 && <Typography>No activities found</Typography>}

              {items.map((it: any, idx: number) => {
                try {
                  if (it.type === 'child-deduction') {
                    const p = it.payload;
                    const userDisplay = p.user ? `${p.user.name} | ${p.user.email}` : p.userId;
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <ListRowCard accent="#DAD6CC" index={idx}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, p: 2 }}>
                        <Box>
                          <Typography variant="body1"><strong>{userDisplay}</strong> deducted <strong>{p.qty}</strong> of <code>{p.sku}</code></Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>for bundle <strong>{p.bundleSku || '-'}</strong></Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{new Date(p.createdAt).toLocaleString()}</Typography>
                          </Box>
                        </ListRowCard>
                      </Box>
                    );
                  }
                  if (it.type === 'bundle-creation') {
                    const p = it.payload;
                    const userDisplay = p.user ? `${p.user.name} | ${p.user.email}` : p.userId;
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <ListRowCard accent="#DC5829" index={idx}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, p: 2 }}>
                        <Box>
                          <Typography variant="body1"><strong>{userDisplay}</strong> created <strong>{p.quantity}</strong> of bundle <code>{p.bundleSku}</code></Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Child SKUs: {p.childSkus?.map((c: any) => `${c.sku}(${c.qty})`).join(', ') || 'N/A'}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{new Date(p.createdAt).toLocaleString()}</Typography>
                          </Box>
                        </ListRowCard>
                      </Box>
                    );
                  }
                  if (it.type === 'debundle') {
                    const p = it.payload;
                    const userDisplay = p.user ? `${p.user.name} | ${p.user.email}` : p.userId;
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <ListRowCard accent="#ef4444" index={idx}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, p: 2 }}>
                        <Box>
                          <Typography variant="body1"><strong>{userDisplay}</strong> debundled <strong>{p.quantity}</strong> of bundle <code>{p.bundleSku}</code></Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Source: {p.sourceShelfLocation || 'N/A'} • Child SKUs: {p.childSkus?.map((c: any) => `${c.sku}(${c.qty})`).join(', ') || 'N/A'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{new Date(p.createdAt).toLocaleString()}</Typography>
                          </Box>
                        </ListRowCard>
                      </Box>
                    );
                  }
                  if (it.type === 'bundle-category-log') {
                    const p = it.payload;
                    const userDisplay = p.user ? `${p.user.name} | ${p.user.email}` : p.userId;
                    const prev = p.details?.previousData;
                    const next = p.details?.newData;
                    const changedFields: { field: string; from: any; to: any }[] = [];
                    if (prev && next) {
                      Object.keys(next).forEach((field) => {
                        const prevVal = Array.isArray(prev[field]) ? prev[field].join(', ') : prev[field];
                        const nextVal = Array.isArray(next[field]) ? next[field].join(', ') : next[field];
                        if (JSON.stringify(prevVal ?? null) !== JSON.stringify(nextVal ?? null)) {
                          changedFields.push({ field, from: prevVal, to: nextVal });
                        }
                      });
                    }
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <ListRowCard accent="#B8451E" index={idx}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, p: 2 }}>
                        <Box>
                          <Typography variant="body1">
                            <strong>{userDisplay}</strong> {getActionDescription(p.action)} <code>{p.bundleSku || p.parentSKU}</code>
                            {p.versionNumber && ` (v${p.versionNumber})`}
                          </Typography>
                          {p.childSkus?.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Childs: {p.childSkus.join(', ')}
                            </Typography>
                          )}
                          {changedFields.length > 0 && (
                            <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {changedFields.map(({ field, from, to }) => (
                                <Typography key={field} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  <strong>{field}</strong>:{' '}
                                  <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>{from === null || from === undefined || from === '' ? '(empty)' : String(from)}</span>
                                  {' → '}
                                  <span style={{ fontWeight: 600 }}>{to === null || to === undefined || to === '' ? '(empty)' : String(to)}</span>
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{new Date(p.createdAt).toLocaleString()}</Typography>
                          </Box>
                        </ListRowCard>
                      </Box>
                    );
                  }
                  return null;
                } catch (err) {
                  console.error('Error rendering activity item:', it, err);
                  return (
                    <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: '#fef2f2', borderRadius: 1 }}>
                      <Typography color="error">Error displaying activity item</Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          )}
        </CardContent>
      </Card>
      <TablePaginationBar
        page={page - 1}
        total={filter === 'image-generation' ? imageLogsTotal : totalItems}
        rowsPerPage={DEFAULT_PAGE_SIZE}
        onPageChange={(p) => setPage(p + 1)}
      />
    </PageContainer>
  );
};

export default ActivityLogsPage;
